import { useCallback, useState } from 'react';
import {
  buscarLigas,
  buscarLigaPorId,
  buscarLigaPorUsername,
  buscarLigasPorNome,
  buscarLigasFiltradas,
  criarLiga,
  atualizarLiga,
  atualizarImagemLiga,
  atualizarBannerLiga,
  deletarLiga,
} from '../services/ligaService';
import type { Liga, LigaInput, LigaConnection, LigaFiltros } from '../types/liga';

export function useLiga() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeWithLoading = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err: any) {
      const message = err?.message || 'Erro inesperado';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const listar = useCallback(() => 
    executeWithLoading(() => buscarLigas()), 
    [executeWithLoading]
  );

  const buscarPorId = useCallback((id: string) => 
    executeWithLoading(() => buscarLigaPorId(id)), 
    [executeWithLoading]
  );

  const buscarPorUsername = useCallback((username: string) => 
    executeWithLoading(() => buscarLigaPorUsername(username)), 
    [executeWithLoading]
  );

  const buscarPorNome = useCallback((
    nome: string,
    page?: number,
    size?: number,
    sortBy?: string,
    direction?: string
  ) => 
    executeWithLoading(() => buscarLigasPorNome(nome, page, size, sortBy, direction)), 
    [executeWithLoading]
  );

  const buscarFiltradas = useCallback((
    filtros: LigaFiltros,
    page?: number,
    size?: number,
    sortBy?: string,
    direction?: string
  ) => 
    executeWithLoading(() => buscarLigasFiltradas(filtros, page, size, sortBy, direction)), 
    [executeWithLoading]
  );

  const criar = useCallback((input: LigaInput) => 
    executeWithLoading(() => criarLiga(input)), 
    [executeWithLoading]
  );

  const atualizar = useCallback((id: string, input: LigaInput) => 
    executeWithLoading(() => atualizarLiga(id, input)), 
    [executeWithLoading]
  );

  const deletar = useCallback((id: string) => 
    executeWithLoading(() => deletarLiga(id)), 
    [executeWithLoading]
  );

  const atualizarImagem = useCallback((id: string, imagemUrl: string) => 
    executeWithLoading(() => atualizarImagemLiga(id, imagemUrl)), 
    [executeWithLoading]
  );

  const atualizarBanner = useCallback((id: string, bannerUrl: string) => 
    executeWithLoading(() => atualizarBannerLiga(id, bannerUrl)), 
    [executeWithLoading]
  );

  return {
    loading,
    error,
    listar,
    buscarPorId,
    buscarPorUsername,
    buscarPorNome,
    buscarFiltradas,
    criar,
    atualizar,
    atualizarImagem,
    atualizarBanner,
    deletar,
  } as const;
}

export default useLiga;